import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const DataStructures: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('dataStructures');
  const videoRef = useVideoProgress('dataStructures');
  const [activeSection, setActiveSection] = React.useState<string | null>(null);

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>
        
        {/* Title and description */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Data Structures</h1>
          <p className="text-white/70 text-lg">Learn about fundamental data structures and their applications in computer science.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Are Data Structures?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Data structures are specialized formats for organizing, processing, retrieving, and storing data. 
                They are fundamental building blocks in computer science that help organize and store data efficiently.
              </p>
              <p>
                Think of data structures as different ways to organize your data, each with its own strengths and use cases. 
                Just as you might use different containers to store different items (a backpack for books, a lunchbox for food), 
                in programming, we use different data structures to store and organize different types of data efficiently.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Key Concepts</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Organization:</strong> How data is arranged in memory</li>
                  <li><strong>Access:</strong> How data can be retrieved</li>
                  <li><strong>Operations:</strong> What can be done with the data</li>
                  <li><strong>Efficiency:</strong> How well the structure performs operations</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Core Concepts - Stacked Vertically */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Core Concepts</h2>
            <div className="space-y-6">
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Memory Organization</h3>
                <p className="text-white/70 mb-4">
                  Data structures can be organized in two main ways:
                </p>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Contiguous Memory:</strong> Elements stored next to each other (Arrays)</li>
                  <li><strong>Non-contiguous Memory:</strong> Elements can be anywhere in memory (Linked Lists)</li>
                </ul>
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Contiguous Memory (Array)
[1][2][3][4][5]  // Elements stored next to each other

// Non-contiguous Memory (Linked List)
[1] -> [2] -> [3] -> [4] -> [5]  // Elements can be anywhere in memory`}</code>
                  </pre>
                </div>
              </div>

              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Access Patterns</h3>
                <p className="text-white/70 mb-4">
                  Different data structures support different access patterns:
                </p>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Random Access:</strong> Direct access to any element (Arrays)</li>
                  <li><strong>Sequential Access:</strong> Must traverse to reach elements (Linked Lists)</li>
                  <li><strong>Last-In-First-Out (LIFO):</strong> Most recent element first (Stacks)</li>
                  <li><strong>First-In-First-Out (FIFO):</strong> Oldest element first (Queues)</li>
                </ul>
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Random Access (O(1))
array[3]  // Direct access to any element

// Sequential Access (O(n))
current = head
while (current) {
  current = current.next
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Common Data Structures */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Common Data Structures</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Arrays */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('arrays')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Arrays</h3>
                  <div className="text-2xl">📦</div>
                </div>
              <p className="text-white/70 mb-3">
                  Contiguous memory blocks for storing elements of the same type.
              </p>
              <ul className="list-disc list-inside text-white/60 space-y-1">
                <li>O(1) access time</li>
                  <li>Fixed size in many languages</li>
                  <li>Simple and efficient</li>
              </ul>
                
                {activeSection === 'arrays' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// Creating an array
const numbers = [1, 2, 3, 4, 5];

// Accessing elements
const first = numbers[0];  // O(1)

// Modifying elements
numbers[2] = 10;  // O(1)

// Adding/Removing elements
numbers.push(6);  // O(1) amortized
numbers.pop();    // O(1)`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
            </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* Linked Lists */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('linked-lists')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Linked Lists</h3>
                  <div className="text-2xl">⛓️</div>
                </div>
              <p className="text-white/70 mb-3">
                  Dynamic data structure with nodes connected by pointers.
              </p>
              <ul className="list-disc list-inside text-white/60 space-y-1">
                <li>O(n) access time</li>
                <li>Dynamic size</li>
                  <li>Efficient insertions/deletions</li>
              </ul>
                
                {activeSection === 'linked-lists' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// Node structure
class Node {
  constructor(value) {
    this.value = value;
    this.next = null;
  }
}

// Creating a linked list
const head = new Node(1);
head.next = new Node(2);
head.next.next = new Node(3);

// Traversing
let current = head;
while (current) {
  console.log(current.value);
  current = current.next;
}`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
            </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* Stacks */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('stacks')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Stacks</h3>
                  <div className="text-2xl">📚</div>
                </div>
              <p className="text-white/70 mb-3">
                  LIFO (Last In, First Out) data structure.
              </p>
              <ul className="list-disc list-inside text-white/60 space-y-1">
                  <li>O(1) push/pop operations</li>
                  <li>Great for function calls</li>
                  <li>Undo/redo functionality</li>
              </ul>
                
                {activeSection === 'stacks' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// Stack implementation
class Stack {
  constructor() {
    this.items = [];
  }
  
  push(element) {
    this.items.push(element);
  }
  
  pop() {
    if (this.isEmpty()) return null;
    return this.items.pop();
  }
  
  peek() {
    return this.items[this.items.length - 1];
  }
  
  isEmpty() {
    return this.items.length === 0;
  }
}`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
            </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* Trees */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('trees')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Trees</h3>
                  <div className="text-2xl">🌳</div>
                </div>
              <p className="text-white/70 mb-3">
                  Hierarchical structure with nodes connected by edges.
              </p>
              <ul className="list-disc list-inside text-white/60 space-y-1">
                <li>O(log n) operations for balanced trees</li>
                <li>Natural for hierarchical data</li>
                <li>Many specialized variants</li>
              </ul>
                
                {activeSection === 'trees' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// Binary Tree Node
class TreeNode {
  constructor(value) {
    this.value = value;
    this.left = null;
    this.right = null;
  }
}

// Creating a binary tree
const root = new TreeNode(1);
root.left = new TreeNode(2);
root.right = new TreeNode(3);
root.left.left = new TreeNode(4);
root.left.right = new TreeNode(5);

// In-order traversal
function inOrderTraversal(node) {
  if (!node) return;
  inOrderTraversal(node.left);
  console.log(node.value);
  inOrderTraversal(node.right);
}`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.section>

          {/* Time Complexity */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Time Complexity</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Understanding the time complexity of operations on different data structures is crucial for choosing the right one for your problem.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-white/70">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left">Operation</th>
                      <th className="py-3 px-4 text-left">Array</th>
                      <th className="py-3 px-4 text-left">Linked List</th>
                      <th className="py-3 px-4 text-left">Stack</th>
                      <th className="py-3 px-4 text-left">Queue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Access</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Search</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Insertion</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Deletion</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                  </tbody>
                </table>
            </div>
          </div>
          </motion.section>

          {/* Why Are They Important? */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
          <h2 className="text-2xl font-semibold text-white mb-4">Why Are They Important?</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
            <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Efficient data organization and retrieval:</strong> Choosing the right data structure can dramatically improve performance.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Optimized algorithm implementation:</strong> Many algorithms rely on specific data structures for optimal performance.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Better space and time complexity:</strong> Proper data structure selection reduces both memory usage and execution time.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Foundation for complex applications:</strong> Understanding data structures is essential for building sophisticated software.</span>
                </li>
            </ul>
          </div>
          </motion.section>

          {/* Real-world Applications */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Real-world Applications</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Databases</h3>
                <p className="text-white/70">
                  B-trees and hash tables for efficient data storage and retrieval in databases.
                </p>
              </div>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Web Development</h3>
                <p className="text-white/70">
                  Stacks for function calls, queues for task scheduling, and trees for DOM structure.
                </p>
              </div>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">AI & Machine Learning</h3>
                <p className="text-white/70">
                  Graphs for neural networks, trees for decision trees, and arrays for tensors.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/merp80.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Now that you understand the basics of data structures, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Practice implementing different data structures</li>
                <li>Learn about more advanced structures like graphs and hash tables</li>
                <li>Study algorithms that work with these data structures</li>
                <li>Apply your knowledge to solve real-world problems</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => navigate('/quiz/data-structures')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Take Quiz</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default DataStructures;
